import { useState, useMemo, useEffect } from 'react'
import { Player } from '../types'
import { useAuthStore, useWatchlistStore } from '../stores'
import { extractYouTubeId } from '../utils/security'
import { notifyProfileViewed, notifyProfileSaved } from '../utils/notify'
import { useVideoStore } from '../stores/useVideoStore'
import { useAchievementStore } from '../stores/useAchievementStore'
import PlayerRating from './PlayerRating'
import PitchDiagram from './PitchDiagram'
import { calculateAge } from '../utils/age'
import {
  ChevronLeft, Star, User, BarChart3, History, Video, Camera, Award,
  MessageSquare, Bookmark, Share2, Download, MapPin, Calendar, Shirt,
  Ruler, Weight, Footprints, CheckCircle2, X, Play
} from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'

type Section = 'overview' | 'statistics' | 'career' | 'clips' | 'gallery' | 'achievements' | 'rating'

const SECTIONS: { id: Section; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <User size={16} /> },
  { id: 'statistics', label: 'Statistics', icon: <BarChart3 size={16} /> },
  { id: 'career', label: 'Career', icon: <History size={16} /> },
  { id: 'clips', label: 'Match Clips', icon: <Video size={16} /> },
  { id: 'gallery', label: 'Gallery', icon: <Camera size={16} /> },
  { id: 'achievements', label: 'Achievements', icon: <Award size={16} /> },
  { id: 'rating', label: 'Rating', icon: <Star size={16} /> },
]

interface PublicPlayerProfileProps {
  player: Player
  onBack: () => void
}

function PublicPlayerProfile({ player, onBack }: PublicPlayerProfileProps) {
  const { user } = useAuthStore()
  const { addToWatchlist, removeFromWatchlist, isInWatchlist } = useWatchlistStore()
  const { videos, fetchVideos } = useVideoStore()
  const { achievements, fetchAchievements } = useAchievementStore()
  const [activeSection, setActiveSection] = useState<Section>('clips')
  const [copied, setCopied] = useState(false)
  const [galleryOpen, setGalleryOpen] = useState<number | null>(null)

  const isSaved = isInWatchlist(player.id)
  const isGuest = !user

  useEffect(() => {
    if (player.id) {
      fetchVideos(player.id)
      fetchAchievements(player.id)
    }
  }, [player.id, fetchVideos, fetchAchievements])

  // Notify when anyone views this profile
  useEffect(() => {
    if (player.id) {
      if (user && (user.role === 'scout' || user.role === 'club' || user.role === 'admin')) {
        notifyProfileViewed(
          player.id,
          user.name,
          user.role as 'scout' | 'club' | 'admin',
          user.id
        )
      } else {
        // Guest / anonymous view
        notifyProfileViewed(player.id, 'Guest', 'player', undefined)
      }
    }
  }, [player.id])

  const youtubeId = player.youtube ? extractYouTubeId(player.youtube) : null

  const careerHistory = player.career_history || []
  const matchHistory = player.match_history || []
  const competitionPerformances = player.competition_performances || []

  const allPhotos = useMemo(() => {
    const photos: string[] = []
    if (player.photo_url) photos.push(player.photo_url)
    if (player.full_body_photo_url) photos.push(player.full_body_photo_url)
    if (player.action_photos_urls?.length) photos.push(...player.action_photos_urls)
    return photos
  }, [player])

  const chartData = useMemo(() => {
    return matchHistory.map((m) => ({
      name: new Date(m.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      goals: m.stats?.goals || 0,
      assists: m.stats?.assists || 0,
    })).reverse()
  }, [matchHistory])

  const handleShare = () => {
    const url = new URL(window.location.origin + window.location.pathname)
    url.hash = `#/player-profile/${player.id}`
    navigator.clipboard.writeText(url.toString())
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleSave = () => {
    if (isSaved) {
      removeFromWatchlist(player.id)
    } else {
      addToWatchlist(player.id)
      // Notify the player that their profile was saved
      if (user && (user.role === 'scout' || user.role === 'club' || user.role === 'admin')) {
        notifyProfileSaved(player.id, user.name, user.role as 'scout' | 'club' | 'admin', user.id)
      }
    }
  }

  const handleDownloadPDF = () => {
    window.print()
  }

  const handleMessage = () => {
    if (isGuest) return
    // Navigate to messages with this player context
    window.location.hash = '#/player-dashboard'
  }

  const renderSection = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div className="space-y-8 animate-in fade-in duration-300">
            {/* Hero Banner */}
            <div className="bg-[#021a1a] rounded-[32px] p-6 sm:p-8 border border-white/5">
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-full md:w-72 shrink-0">
                  <div className="aspect-[3/4] rounded-[24px] overflow-hidden bg-gray-900 border-2 border-transparent hover:border-[#7cff01]/20 transition-all duration-500">
                    <img
                      src={player.photo_url || '/placeholder-user.png'}
                      alt={player.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className="flex-1 space-y-6">
                  <div>
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      {player.is_verified && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#7cff01]/10 text-[#7cff01] text-[10px] font-black rounded-full border border-[#7cff01]/20 uppercase tracking-widest">
                          <CheckCircle2 size={12} />
                          Verified
                        </span>
                      )}
                      {player.is_top_talent && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500/10 text-amber-400 text-[10px] font-black rounded-full border border-amber-500/20 uppercase tracking-widest">
                          <Award size={12} />
                          Top Talent
                        </span>
                      )}
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tighter leading-none italic uppercase">
                      {player.name}
                    </h1>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {[
                      { label: 'Position', value: player.main_position || 'N/A' },
                      { label: 'Age', value: player.date_of_birth ? `${calculateAge(player.date_of_birth)} yrs` : `${player.age} yrs` },
                      { label: 'Club', value: player.club || 'Free Agent' },
                      { label: 'League', value: player.league || 'N/A' },
                    ].map((item) => (
                      <div key={item.label} className="bg-[#112b2b]/50 rounded-2xl p-4 border border-white/5">
                        <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">{item.label}</p>
                        <p className="text-white font-bold text-sm truncate">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  {player.biography && (
                    <div className="bg-white/2 p-5 rounded-2xl border border-white/5">
                      <p className="text-gray-300 text-sm leading-relaxed italic">{player.biography}</p>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-3">
                    {[
                      { label: 'Height', value: player.height ? `${player.height}cm` : null, icon: Ruler },
                      { label: 'Weight', value: player.weight ? `${player.weight}kg` : null, icon: Weight },
                      { label: 'Foot', value: player.foot || null, icon: Footprints },
                      { label: 'Jersey', value: player.jersey_number ? `#${player.jersey_number}` : null, icon: Shirt },
                      { label: 'DOB', value: player.date_of_birth ? new Date(player.date_of_birth).toLocaleDateString() : null, icon: Calendar },
                    ].filter(i => i.value).map((item) => (
                      <div key={item.label} className="flex items-center gap-2 px-3 py-2 bg-white/5 rounded-xl border border-white/5">
                        <item.icon size={14} className="text-gray-500" />
                        <span className="text-xs text-gray-300"><span className="text-gray-500 uppercase text-[10px] font-black tracking-widest">{item.label}:</span> {item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Goals', value: player.goals || 0, color: 'text-[#7cff01]' },
                { label: 'Assists', value: player.assists || 0, color: 'text-blue-400' },
                { label: 'Matches', value: player.apps || 0, color: 'text-white' },
                { label: 'Minutes', value: player.mins || 0, color: 'text-white' },
              ].map((stat) => (
                <div key={stat.label} className="bg-[#021a1a] border border-white/5 rounded-3xl p-5 flex flex-col items-center justify-center text-center">
                  <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">{stat.label}</p>
                  <p className={`text-3xl sm:text-4xl font-black ${stat.color}`}>{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Pitch Diagram */}
            {(player.positions && player.positions.length > 0) || player.main_position ? (
              <div className="bg-[#021a1a] rounded-3xl p-6 border border-white/5">
                <div className="flex items-center gap-2 mb-4">
                  <h3 className="text-xs font-black text-white uppercase tracking-widest">Position Map</h3>
                </div>
                <div className="max-w-[240px] mx-auto">
                  <PitchDiagram
                    positions={player.positions || (player.main_position ? [player.main_position] : [])}
                    mainPosition={player.main_position}
                  />
                </div>
              </div>
            ) : null}

            {/* Agent / Manager — Scout & Club only */}
            {user && (user.role === 'scout' || user.role === 'club' || user.role === 'admin') && (
              <div className={`rounded-3xl p-6 border ${
                player.has_agent && player.agent_name
                  ? 'bg-[#021a1a] border-[#7cff01]/20'
                  : 'bg-red-900/5 border-red-500/20'
              }`}>
                <div className="flex items-center gap-2 mb-4">
                  <User size={16} className={player.has_agent && player.agent_name ? 'text-[#7cff01]' : 'text-red-400'} />
                  <h3 className="text-xs font-black text-white uppercase tracking-widest">Agent / Manager</h3>
                  {!player.has_agent && (
                    <span className="ml-auto px-3 py-1 bg-red-500/10 border border-red-500/20 text-red-400 text-[10px] font-black uppercase tracking-widest rounded-full">
                      NO AGENT
                    </span>
                  )}
                </div>
                {player.has_agent && player.agent_name ? (
                  <div className="space-y-3">
                    <div className="bg-white/2 rounded-2xl p-4 border border-white/5">
                      <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">Agent Name</p>
                      <p className="text-white font-bold">{player.agent_name}</p>
                    </div>
                    {player.agent_contact && (
                      <div className="bg-white/2 rounded-2xl p-4 border border-white/5">
                        <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">Contact</p>
                        <p className="text-[#7cff01] font-bold">{player.agent_contact}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-red-900/5 border border-dashed border-red-500/20 rounded-2xl p-6 text-center">
                    <p className="text-red-400 text-xs font-black uppercase tracking-widest">No Agent / Manager</p>
                    <p className="text-gray-500 text-[10px] mt-1 uppercase tracking-widest">This player has indicated they have no representative</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )

      case 'statistics':
        return (
          <div className="space-y-8 animate-in fade-in duration-300">
            {/* Overview Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Goals', value: player.goals || 0 },
                { label: 'Assists', value: player.assists || 0 },
                { label: 'Yellow Cards', value: player.yellow_cards || 0 },
                { label: 'Red Cards', value: player.red_cards || 0 },
                { label: 'Key Passes', value: player.kp || 0 },
                { label: 'Dribbles', value: player.dr || 0 },
                { label: 'Duels Won', value: player.du || 0 },
                { label: 'Shots', value: player.shots || 0 },
              ].map((stat) => (
                <div key={stat.label} className="bg-[#021a1a] border border-white/5 rounded-3xl p-5 text-center">
                  <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">{stat.label}</p>
                  <p className="text-3xl font-black text-[#7cff01]">{stat.value}</p>
                </div>
              ))}
            </div>

            {/* Goals & Assists Chart */}
            {chartData.length > 1 && (
              <div className="bg-[#021a1a] rounded-3xl p-6 border border-white/5">
                <h3 className="text-xs font-black text-white uppercase tracking-widest mb-6">Goals & Assists Trend</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={chartData} margin={{ top: 0, right: 0, left: -40, bottom: 0 }}>
                    <XAxis dataKey="name" hide />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} axisLine={false} tickLine={false} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#021a1a', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '12px' }}
                    />
                    <Line type="monotone" dataKey="goals" stroke="#7cff01" strokeWidth={3} dot={false} />
                    <Line type="monotone" dataKey="assists" stroke="#3b82f6" strokeWidth={3} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Skills Assessment */}
            <div className="bg-[#021a1a] rounded-3xl p-6 border border-white/5">
              <h3 className="text-xs font-black text-white uppercase tracking-widest mb-6">Skills Assessment</h3>
              <div className="space-y-5">
                {[
                  { label: 'Technical', value: player.technical || 0, color: 'bg-[#7cff01]' },
                  { label: 'Physical', value: player.physical || 0, color: 'bg-blue-400' },
                  { label: 'Mental', value: player.mental || 0, color: 'bg-purple-400' },
                ].map((skill) => (
                  <div key={skill.label} className="space-y-2">
                    <div className="flex justify-between text-[10px] font-black uppercase tracking-widest">
                      <span className="text-gray-500">{skill.label}</span>
                      <span className="text-white">{skill.value}/10</span>
                    </div>
                    <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${skill.color} rounded-full transition-all duration-1000`}
                        style={{ width: `${(skill.value || 5) * 10}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Competition Performances */}
            {competitionPerformances.length > 0 && (
              <div className="bg-[#021a1a] rounded-3xl p-6 border border-white/5">
                <h3 className="text-xs font-black text-white uppercase tracking-widest mb-6">Competition Records</h3>
                <div className="space-y-3">
                  {competitionPerformances.map((perf) => (
                    <div key={perf.id} className="bg-[#112b2b]/30 rounded-2xl p-4 border border-white/5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-white font-bold">{perf.competition_name}</p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest">{perf.appearance_date || 'No date'}</p>
                        </div>
                        {perf.rating && (
                          <span className="px-2 py-1 bg-[#7cff01]/10 text-[#7cff01] text-xs font-black rounded-full">
                            {perf.rating}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <div className="text-center">
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest">Goals</p>
                          <p className="text-lg font-black text-[#7cff01]">{perf.goals || 0}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest">Assists</p>
                          <p className="text-lg font-black text-blue-400">{perf.assists || 0}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest">Mins</p>
                          <p className="text-lg font-black text-white">{perf.minutes || 0}</p>
                        </div>
                        <div className="text-center">
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest">Opponent</p>
                          <p className="text-sm font-bold text-white truncate">{perf.opponent || 'N/A'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )

      case 'career':
        return (
          <div className="space-y-6 animate-in fade-in duration-300">
            <div className="bg-[#021a1a] rounded-3xl p-6 border border-white/5">
              <div className="flex items-center gap-2 mb-6">
                <History size={16} className="text-[#7cff01]" />
                <h3 className="text-xs font-black text-white uppercase tracking-widest">Career History</h3>
              </div>

              {careerHistory.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-white/10 bg-black/20 p-8 text-center">
                  <p className="text-gray-500 text-xs font-black uppercase tracking-widest">No career history available</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {careerHistory.map((entry, idx) => (
                    <div key={entry.id} className="relative flex gap-4">
                      {/* Timeline Line */}
                      <div className="flex flex-col items-center">
                        <div className={`w-4 h-4 rounded-full ${idx === 0 ? 'bg-[#7cff01]' : 'bg-gray-700'} border-2 border-[#021a1a] z-10`} />
                        {idx < careerHistory.length - 1 && (
                          <div className="w-0.5 flex-1 bg-gray-800" />
                        )}
                      </div>

                      {/* Card */}
                      <div className="flex-1 bg-[#112b2b]/30 rounded-2xl p-5 border border-white/5 mb-2">
                        <div className="flex items-start justify-between gap-4 mb-3">
                          <div className="min-w-0">
                            <p className="text-white font-bold text-lg truncate">{entry.club || 'N/A'}</p>
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest">{entry.years || 'N/A'}</p>
                          </div>
                          {entry.transaction_fee?.trim() && (
                            <span className="shrink-0 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-[#7cff01]/20 bg-[#7cff01]/10 text-[#7cff01]">
                              {entry.transaction_fee}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div className="bg-black/30 rounded-xl p-3 text-center">
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Goals</p>
                            <p className="text-2xl font-black text-[#7cff01]">{entry.goals ?? 0}</p>
                          </div>
                          <div className="bg-black/30 rounded-xl p-3 text-center">
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest mb-1">Assists</p>
                            <p className="text-2xl font-black text-blue-400">{entry.assists ?? 0}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Match History */}
            {matchHistory.length > 0 && (
              <div className="bg-[#021a1a] rounded-3xl p-6 border border-white/5">
                <h3 className="text-xs font-black text-white uppercase tracking-widest mb-6">Match History</h3>
                <div className="space-y-3">
                  {matchHistory.slice(0, 10).map((match) => (
                    <div key={match.id} className="bg-[#112b2b]/30 rounded-2xl p-4 border border-white/5">
                      <div className="flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-bold truncate">{match.opponent || 'Unknown'}</p>
                          <p className="text-[10px] text-gray-500 uppercase tracking-widest">{match.competition || 'Match'} • {new Date(match.date).toLocaleDateString()}</p>
                        </div>
                        <div className="flex items-center gap-4 ml-4">
                          <div className="text-center">
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest">G</p>
                            <p className="text-lg font-black text-[#7cff01]">{match.stats?.goals || 0}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest">A</p>
                            <p className="text-lg font-black text-blue-400">{match.stats?.assists || 0}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-[10px] text-gray-500 uppercase tracking-widest">Min</p>
                            <p className="text-lg font-black text-white">{match.stats?.mins || 0}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )

      case 'clips':
        return (
          <div className="space-y-6 animate-in fade-in duration-300">
            {/* Featured Video */}
            {youtubeId && (
              <div className="bg-[#021a1a] rounded-[32px] overflow-hidden border border-white/5">
                <div className="p-4 bg-white/5 flex items-center justify-between">
                  <span className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Featured Highlights</span>
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                </div>
                <div className="aspect-video bg-black">
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://www.youtube.com/embed/${youtubeId}?rel=0&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
                    title="Player Highlights"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* Database Match Videos */}
            {videos.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">Match Clips</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {videos.map((video) => (
                    <div key={video.id} className="bg-[#021a1a] rounded-2xl overflow-hidden border border-white/5">
                      <div className="relative aspect-video bg-black">
                        {video.thumbnail_url ? (
                          <img src={video.thumbnail_url} alt={video.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex items-center justify-center h-full">
                            <Video size={32} className="text-gray-600" />
                          </div>
                        )}
                        {video.url.includes('youtube.com') && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                              <Play size={20} className="text-white ml-1" fill="white" />
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="p-3">
                        <p className="text-sm font-bold text-white truncate">{video.title}</p>
                        {video.opponent && (
                          <p className="text-xs text-gray-400 mt-1">vs {video.opponent}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {video.competition && (
                            <span className="text-[10px] px-2 py-0.5 bg-white/5 rounded-full text-gray-400">{video.competition}</span>
                          )}
                          {video.match_date && (
                            <span className="text-[10px] text-gray-500">
                              {new Date(video.match_date).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <a
                          href={video.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 flex items-center gap-2 text-xs font-bold text-[#7cff01] hover:underline"
                        >
                          <Play size={12} /> Watch Video
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!youtubeId && videos.length === 0 && (
              <div className="bg-[#021a1a] rounded-3xl p-12 border border-white/5 text-center">
                <Video className="mx-auto text-gray-700 mb-4" size={48} />
                <p className="text-gray-500 text-xs font-black uppercase tracking-widest">No match clips available</p>
              </div>
            )}
          </div>
        )

      case 'gallery':
        return (
          <div className="space-y-6 animate-in fade-in duration-300">
            {allPhotos.length === 0 ? (
              <div className="bg-[#021a1a] rounded-3xl p-12 border border-white/5 text-center">
                <Camera className="mx-auto text-gray-700 mb-4" size={48} />
                <p className="text-gray-500 text-xs font-black uppercase tracking-widest">No photos available</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {allPhotos.map((photo, i) => (
                  <button
                    key={i}
                    onClick={() => setGalleryOpen(i)}
                    className="aspect-square rounded-[24px] overflow-hidden bg-gray-900 border border-white/5 hover:border-[#7cff01]/20 transition-all group"
                  >
                    <img
                      src={photo}
                      alt={`${player.name} photo ${i + 1}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Lightbox */}
            {galleryOpen !== null && allPhotos[galleryOpen] && (
              <div
                className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
                onClick={() => setGalleryOpen(null)}
              >
                <button
                  onClick={() => setGalleryOpen(null)}
                  className="absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition"
                >
                  <X size={20} />
                </button>
                <img
                  src={allPhotos[galleryOpen]}
                  alt={`${player.name} photo`}
                  className="max-w-full max-h-[85vh] object-contain rounded-2xl"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}
          </div>
        )

      case 'achievements':
        return (
          <div className="space-y-6 animate-in fade-in duration-300">
            {achievements.length === 0 ? (
              <div className="bg-[#021a1a] rounded-3xl p-12 border border-white/5 text-center">
                <Award className="mx-auto text-gray-700 mb-4" size={48} />
                <p className="text-gray-500 text-xs font-black uppercase tracking-widest">No achievements recorded</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {achievements.map((ach: any) => (
                  <div key={ach.id} className="bg-[#021a1a] rounded-3xl p-5 border border-white/5 hover:border-[#7cff01]/20 transition">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-[#7cff01]/10 flex items-center justify-center flex-shrink-0">
                        <Award size={18} className="text-[#7cff01]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white font-bold truncate">{ach.title}</p>
                        {ach.description && (
                          <p className="text-xs text-gray-400 mt-1 line-clamp-2">{ach.description}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          {ach.year && (
                            <span className="text-[10px] text-gray-500 uppercase tracking-widest">{ach.year}</span>
                          )}
                          {ach.club && (
                            <span className="text-[10px] text-gray-500 uppercase tracking-widest">• {ach.club}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )

      case 'rating':
        return (
          <div className="animate-in fade-in duration-300">
            <PlayerRating player={player} />
          </div>
        )
    }
  }

  return (
    <div className="max-w-6xl mx-auto pb-20 animate-in fade-in duration-500">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-[#7cff01] hover:text-[#8eff1a] mb-8 transition font-black uppercase tracking-widest text-xs"
      >
        <ChevronLeft size={18} />
        Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          {/* Player Card */}
          <div className="bg-[#021a1a] rounded-[32px] p-6 border border-white/5 text-center">
            <div className="w-full aspect-square rounded-[32px] overflow-hidden bg-gray-900 mb-6 border-2 border-transparent hover:border-[#7cff01]/20 transition-all duration-500 shadow-2xl">
              <img
                src={player.photo_url || '/placeholder-user.png'}
                alt={player.name}
                className="w-full h-full object-cover"
              />
            </div>

            <h2 className="text-xl font-black text-white tracking-tight italic uppercase mb-2">{player.name}</h2>
            <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-4">{player.main_position}</p>

            <div className="flex justify-center gap-1 mb-4">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star
                  key={s}
                  size={16}
                  className={`${s <= (player.potential || 0) ? 'text-[#7cff01] fill-[#7cff01]' : 'text-gray-800 fill-transparent'}`}
                />
              ))}
            </div>

            <div className="space-y-3 pt-4 border-t border-white/5 text-left">
              <div className="flex items-center gap-2 text-sm">
                <MapPin size={14} className="text-gray-500 flex-shrink-0" />
                <span className="text-white">{player.state}{player.lga ? `, ${player.lga}` : ''}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Calendar size={14} className="text-gray-500 flex-shrink-0" />
                <span className="text-white">{player.age} years old</span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            {!isGuest && (
              <button
                onClick={handleMessage}
                className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-[#7cff01] hover:bg-[#8eff1a] text-black rounded-2xl font-black uppercase text-xs transition shadow-lg shadow-[#7cff01]/10"
              >
                <MessageSquare size={16} />
                Message Player
              </button>
            )}

            {!isGuest && (
              <button
                onClick={handleSave}
                className={`w-full flex items-center justify-center gap-2 px-4 py-4 rounded-2xl font-black uppercase text-xs transition border ${
                  isSaved
                    ? 'bg-[#7cff01]/10 border-[#7cff01]/20 text-[#7cff01]'
                    : 'bg-white/5 border-white/5 text-white hover:bg-white/10'
                }`}
              >
                <Bookmark size={16} className={isSaved ? 'fill-current' : ''} />
                {isSaved ? 'Saved' : 'Save Player'}
              </button>
            )}

            <button
              onClick={handleShare}
              className={`w-full flex items-center justify-center gap-2 px-4 py-4 rounded-2xl font-black uppercase text-xs transition border ${
                copied
                  ? 'bg-[#7cff01]/10 border-[#7cff01]/20 text-[#7cff01]'
                  : 'bg-white/5 border-white/5 text-white hover:bg-white/10'
              }`}
            >
              <Share2 size={16} />
              {copied ? 'Link Copied!' : 'Share Profile'}
            </button>

            <button
              onClick={handleDownloadPDF}
              className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-white/5 border border-white/5 text-white hover:bg-white/10 rounded-2xl font-black uppercase text-xs transition"
            >
              <Download size={16} />
              Download PDF
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* Section Tabs */}
          <div className="flex gap-1 overflow-x-auto whitespace-nowrap hide-scrollbar bg-[#021a1a] rounded-2xl p-1 border border-white/5">
            {SECTIONS.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-bold transition whitespace-nowrap flex-shrink-0 ${
                  activeSection === section.id
                    ? 'bg-[#7cff01] text-black'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {section.icon}
                {section.label}
              </button>
            ))}
          </div>

          {/* Section Content */}
          {renderSection()}
        </div>
      </div>
    </div>
  )
}

export default PublicPlayerProfile
